import { type Component, type ExtractPropTypes } from 'vue';

/**
 * How an opened entry is presented: a centered box or an edge-anchored drawer.
 */
export enum ModalMode {
    Modal = 'modal',
    Drawer = 'drawer',
}

/**
 * Screen edge a drawer is anchored to and slides in from.
 */
export enum DrawerSide {
    Left = 'left',
    Right = 'right',
}

/**
 * Responsive width presets supported by the shared drawer panel.
 */
export enum DrawerSize {
    Default = 'default',
    Large = 'large',
}

/**
 * Component with emits - a helper for the TS to infer the emits properly.
 */
export type ComponentWithEmits = Component & { emits?: unknown };

type ComponentEmitNames<T extends ComponentWithEmits> = T extends {
    emits?: infer ComponentEmits;
}
    ? NonNullable<ComponentEmits> extends readonly (infer EventName)[]
        ? Extract<EventName, string>
        : NonNullable<ComponentEmits> extends Record<string, unknown>
        ? Extract<keyof NonNullable<ComponentEmits>, string>
        : string
    : string;

export type ComponentEmitHandlers<T extends ComponentWithEmits = ComponentWithEmits> = Partial<
    Record<ComponentEmitNames<T>, (...args: unknown[]) => void>
>;

/**
 * Definition for a slot in an object form.
 */
export interface ModalSlotOptions<T extends ComponentWithEmits = Component> {
    component: T extends ComponentWithEmits ? T : never;
    props?: ComponentProps<T>;
    emits?: ComponentEmitHandlers<T>;
}

/**
 * Check if a value is a modal slot options object.
 */
export const isModalSlotOptions = (value: unknown): value is ModalSlotOptions => {
    if (typeof value === 'object' && value !== null) {
        return 'component' in value;
    }
    return false;
};

/**
 * Define what can be passed to a slot.
 *
 * Examples:
 * - string:  '<p>Hello from inline slot</p>',
 * - object:
 *     {
 *         component: DemoSlot,
 *         props: { message: 'Custom prop value for middle slot' },
 *         emits: { click: handleClick },
 *     },
 * - component: DemoSlot
 */
export type ModalSlot = string | Component | ModalSlotOptions;

/**
 * Record of slots to pass to the component.
 * For the default slot, use `default` as the key.`
 */
export type ComponentSlots = Record<string, ModalSlot>;

/**
 * Component props - a record of props to pass to the component.
 */
export type ComponentProps<T> = T extends { props: infer P }
    ? ExtractPropTypes<P>
    : Record<string, unknown>;
